// KANALOVERSIKTEN — søkbar liste over PTT-kanaler, med vaktlinje og All Call.
//
// ── HVORFOR DENNE FILA FINNES ───────────────────────────────────────────────
// Eiers bestilling (2026-08-18): *«det viktigste er at vi kan ta ibruk actionknapp
// på iphone og at vi får en ryddig kanaloversikt, som også er søkbar og at man kan
// opprette egne grupper»* — og *«Jeg vil også ha mulighet til å styre GNÅ-brukere
// fra skade inn på en kanal som jeg kan tvinge de til å lytte på. en slags "all
// Call"»*.
//
// `@skade/contracts/pttvakt.ts` er vaktmodellen, og den hadde **null kallsteder**
// til bølge 225 (verifisert per eksportert symbol). Hubben eier nå lista
// (`pttvakt-lager.ts`); dette er der operatøren ser og styrer den.
//
// ── ⚠ TRE TING SOM MÅ VÆRE RIKTIGE PÅ SKJERMEN ──────────────────────────────
//
// 1. **TX MÅ STÅ FØRST OG ALLTID.** Det operatøren må vite før hun trykker er HVOR
//    det går. En liste sortert alfabetisk, med senderetten et sted nedi, er en
//    liste der man sender på feil nett.
//
// 2. **EN PÅLAGT KANAL MÅ SE PÅLAGT UT.** «All Call» satte den, og brukeren kan
//    ikke fjerne den. Sto den som en vanlig rad med en fjern-knapp som ikke
//    virker, ville hun trodd systemet var i stuss.
//
// 3. **«INGEN TX» ER EN ADVARSEL, IKKE EN TOM TILSTAND.** En operatør uten
//    senderett er stum, og hun må se det før hun trenger det — ikke oppdage det
//    når hun holder knappen inne og ingen svarer.
import {
  MAKS_RX, hvaHoresNaa, kanSende, txKanal, taleDegraderingsnivaa, taleDegraderingsLinje,
  type AktivSending, type AllCall, type Hoerbart, type Kanalvakt, type LinkQuality,
  type TaleDegraderingsTilstand,
} from '@skade/contracts';

export interface KanaloversiktData {
  vakter: Kanalvakt[];
  linje: string;
  maksRx: number;
  sisteAllCall: AllCall | null;
  advarsel?: string;
}

export interface KanaloversiktOpts {
  hent: () => Promise<KanaloversiktData | null>;
  /** Sett senderetten. */
  settTx: (kanalId: string) => Promise<string | null>;
  /** Legg til en kanal (egen gruppe). */
  leggTil: (kanalId: string, navn: string) => Promise<string | null>;
  fjern: (kanalId: string) => Promise<string | null>;
  /** All Call. Utelates → knappen bygges ikke (mangler kommandofullmakt). */
  allCall?: (kanalId: string, navn: string, grunn: string, ogsaaTx: boolean) => Promise<string | null>;
  varsle: (t: string) => void;
  /**
   * Egen bruker-id — brukt av `kanSende` til å skille «jeg snakker» fra «en
   * annen snakker». Utelates trygt (defaulter til ''); da vil panelet aldri
   * gjenkjenne en pågående sending som ens egen, men det påvirker ingenting så
   * lenge `hentAktive` heller ikke er koblet (se under).
   */
  meg?: string;
  /**
   * ⚠ HENTER AKTIVE SENDINGER — INGEN EKTE KILDE FINNES I COP PR. NÅ. `hvaHoresNaa`
   * og `kanSende` (contracts) er ferdige og testet (bølge 214), men de trenger
   * `AktivSending[]`: HVEM som sender, PÅ hvilken kanal, NÅ. Det tallet kommer fra
   * LiveKits active-speaker inne i talekanalen — og COP har ingen LiveKit-klient
   * (PTT-05 «COP PTT-panel på samme kanal som GNÅ», fortsatt i `next`, blokkert).
   * Hubben bringer heller ikke dette over — den bygger på Matrix-events, og
   * on-air/remote-on-air lever inne i LiveKit-SFU-en, usett av hubben (verifisert
   * mot `lib/ptt/ptt_channel_engine.dart`).
   *
   * Å late som denne fantes — en tikker som dikter opp «Ivar snakker nå» — ville
   * vært nøyaktig den staselige stubben §1 forbyr. Derfor: utelates parameteren,
   * viser panelet ærlig «stille» (fordi vi faktisk ikke vet om noe høres), IKKE en
   * skjult sannhet. Kobles PTT-05, gis en ekte `hentAktive` her — resten av denne
   * fila er allerede skrevet for den dagen.
   */
  hentAktive?: () => Promise<AktivSending[]>;
  /**
   * ⚠ TALE-DEGRADERINGSNIVÅ (PTT-13) — INGEN EKTE KILDE FINNES I COP PR. NÅ.
   * `taleDegraderingsnivaa` (contracts) er ferdig og testet, og trenger bare en
   * `LinkQuality`-måling av OPERATØRENS EGEN link (samme skala som mesh-panelet,
   * se filhodet i taledegradering.ts). Men COP kjører i nettleser og har ingen
   * RSSI/SNR fra modemet — det tallet finnes i dag bare på GNÅ-enheten, og den
   * strømmer ikke det inn i hubben ennå (samme type gap som `hentAktive` over).
   *
   * Å late som et nivå fantes uten denne — f.eks. vise «full-opus» som en
   * default — ville vært nøyaktig den stille, uriktige degraderingen kortet
   * ber oss unngå. Derfor: utelates parameteren, vises degraderingslinja IKKE
   * (ikke en falsk «full kvalitet»-påstand). Kobles en ekte link-kvalitet-kilde
   * inn her, tegner resten av panelet den med én gang.
   */
  hentTaleLink?: () => Promise<LinkQuality | null>;
}

/**
 * Sorter vaktlista for VISNING: TX først, deretter nød, deretter prioritet.
 *
 * ⚠ IKKE ALFABETISK. Rekkefølgen er operativ: hvor det går, hva som ikke kan
 * ignoreres, og så resten. Et navn er den minst nyttige sorteringsnøkkelen her.
 */
export function sorterForVisning(vakter: readonly Kanalvakt[]): Kanalvakt[] {
  return [...vakter].sort((a, b) => {
    if (a.rolle !== b.rolle) return a.rolle === 'tx' ? -1 : 1;
    if (a.nod !== b.nod) return a.nod ? -1 : 1;
    return a.prioritet - b.prioritet || a.navn.localeCompare(b.navn, 'nb');
  });
}

/**
 * Filtrer på fritekst.
 *
 * ⚠ SKJULER ALDRI TX ELLER NØDKANALEN. Et søk som gjemte senderetten ville latt
 * operatøren sende på et nett hun ikke ser at hun står på — og en nødkanal som
 * forsvinner fordi noen skrev «tropp» i søkefeltet er den verste tenkelige
 * filtreringen. Søket er et VERKTØY for å finne, ikke en bryter som skrur av.
 */
export function filtrerKanaler(vakter: readonly Kanalvakt[], sok: string): Kanalvakt[] {
  const q = sok.trim().toLowerCase();
  if (!q) return [...vakter];
  return vakter.filter((k) =>
    k.rolle === 'tx' || k.nod
    || k.navn.toLowerCase().includes(q)
    || k.kanalId.toLowerCase().includes(q));
}

/** Hvor mange plasser er igjen på vaktlista? */
export function ledigePlasser(vakter: readonly Kanalvakt[], maks = MAKS_RX): number {
  return Math.max(0, maks - vakter.length);
}

/**
 * Statuslinja over lista.
 *
 * ⚠ «INGEN TX» KOMMER FØRST OG MED ⚠. En stum operatør er den ene tilstanden som
 * må rives i øyet — resten (hvor mange plasser som er igjen) er informasjon.
 */
export function kanalStatus(d: KanaloversiktData): { tekst: string; advarsel: boolean } {
  const harTx = d.vakter.some((k) => k.rolle === 'tx');
  const ledig = ledigePlasser(d.vakter, d.maksRx);
  if (!harTx) {
    return {
      tekst: '⚠ INGEN SENDERETT — du kan lytte, men ikke sende. Velg en kanal og trykk «Send her».',
      advarsel: true,
    };
  }
  const deler = [d.linje];
  deler.push(ledig ? `${ledig} plass${ledig === 1 ? '' : 'er'} ledig` : `fullt (${d.maksRx} kanaler)`);
  return { tekst: deler.join(' · '), advarsel: false };
}

/**
 * Kort merke per kanal — til raden.
 *
 * ⚠ TEKST, IKKE BARE FARGE. Skal kunne leses i solskinn med nattfilter, der
 * fargeforskjeller forsvinner først. Samme regel som hastegrads-stripa (bølge 215).
 */
export function kanalMerke(k: Kanalvakt): string {
  if (k.nod) return 'NØD';
  if (k.rolle === 'tx') return 'TX';
  if (k.paatvungetAv) return 'PÅLAGT';
  return 'RX';
}

/**
 * Kan operatøren fjerne kanalen selv?
 *
 * ⚠ FINNES FOR AT KNAPPEN SKAL KUNNE DEAKTIVERES MED EN FORKLARING framfor å feile
 * ved klikk. Tre ting kan ikke fjernes, og alle tre er sikkerhet: nødkanalen (ikke
 * operatørens valg), en pålagt kanal (da var ordren meningsløs), og den siste TX-en
 * (et samband uten senderett er ikke et samband).
 */
export function kanFjerne(k: Kanalvakt, alle: readonly Kanalvakt[]): { kan: boolean; grunn: string } {
  if (k.nod) return { kan: false, grunn: 'Nødkanalen kan ikke fjernes' };
  if (k.paatvungetAv) return { kan: false, grunn: `Pålagt av ${k.paatvungetAv} — kan ikke fjernes` };
  if (k.rolle === 'tx' && alle.length === 1) {
    return { kan: false, grunn: 'Siste kanal med senderett — du ville blitt stum' };
  }
  return { kan: true, grunn: '' };
}

/**
 * Teksten om siste All Call.
 *
 * ⚠ MED ALDER. «Sjefen satte alle på KOMPANI» uten tid er en påstand uten
 * holdbarhetsdato — var det for to minutter siden eller i går?
 */
export function allCallTekst(ac: AllCall | null, naaMs: number): string {
  if (!ac) return '';
  const min = Math.max(0, Math.floor((naaMs - ac.paalagtMs) / 60_000));
  const alder = min < 1 ? 'nå' : min < 60 ? `${min} min siden` : `${Math.floor(min / 60)} t siden`;
  return `${ac.paalagtAv} satte alle på ${ac.navn} (${alder})`
    + `${ac.ogsaaTx ? ' — også senderett' : ''}${ac.grunn ? `: ${ac.grunn}` : ''}`;
}

/**
 * Dualwatch/Triwatch-betegnelsen for vaktlista — det operatøren har et ord for.
 *
 * ⚠ TALLET ER HELE VAKTLISTA (TX + RX), IKKE «TX + opp til MAKS_RX RX». `MAKS_RX`
 * i contracts er taket på LISTAS TOTALE STØRRELSE (se `leggTilVakt`: den teller
 * `liste.length`, som inkluderer TX). «Dualwatch» er to nett i øret — én av dem
 * med senderett — ikke to RX-nett i tillegg til TX.
 */
export function dualwatchOppsummering(
  vakter: readonly Kanalvakt[], maksRx = MAKS_RX,
): { betegnelse: string; antall: number; maksRx: number; txNavn: string | null; rxNavn: string[] } {
  const tx = txKanal(vakter);
  const rxNavn = vakter.filter((k) => k.rolle === 'rx').map((k) => k.navn);
  const antall = vakter.length;
  const betegnelse = antall <= 1 ? 'Enkelkanal'
    : antall === 2 ? 'Dualwatch'
    : antall === 3 ? 'Triwatch'
    // Over doktrinegrensen skjer bare når nødkanalen presset lista utover
    // MAKS_RX (leggTilVakt lar aldri noe annet gjøre det).
    : `${antall} kanaler`;
  return { betegnelse, antall, maksRx, txNavn: tx?.navn ?? null, rxNavn };
}

/**
 * Presedensmerket til det som spiller nå — til badge-fargen på skjermen.
 *
 * ⚠ LEST FRA `h.spiller` OG VAKTLISTA, IKKE TEKST-PARSET FRA `grunn`. `grunn` er
 * satsen skrevet FOR OPERATØREN i `hvaHoresNaa`, og kan endre ordlyd; å
 * gjenkjenne NØD/FLASH ved å lete etter ord i den ville latt en ordlydsendring i
 * `pttvakt.ts` stille slå ut badgen uten at noen testet det.
 */
export function presedensMerke(h: Hoerbart, vakter: readonly Kanalvakt[]): 'nod' | 'flash' | 'pri' | 'stille' {
  if (!h.spiller) return 'stille';
  const v = vakter.find((k) => k.kanalId === h.spiller!.kanalId);
  if (v?.nod) return 'nod';
  if (h.spiller.presedens === 'flash') return 'flash';
  return 'pri';
}

/**
 * Linja: HVEM snakker, og HVORFOR (presedens) hun får ordet.
 *
 * ⚠ «HVORFOR» ER IKKE PYNT. Kravet bak denne linja er at operatøren skal kunne se
 * HVORFOR den som snakker får ordet — ikke bare AT noen gjør det. Uten det ser en
 * nødsending som bryter gjennom ut som et vanlig kanalbytte.
 */
export function hoerbartLinje(h: Hoerbart): string {
  if (!h.spiller) return h.grunn || 'Stille på alle kanaler i vaktlista.';
  return `${h.spiller.fra} snakker — ${h.grunn}`;
}

/**
 * ⚠ FORTRENGT-LINJENE — TAPT TRAFIKK, OG SKAL VARSLES PÅ. Jf. filhodet i
 * `pttvakt.ts`: på en radio HØRER man kollisjonen, digitalt forsvinner den
 * svakeste i stillhet uten dette. Hver linje sier HVEM og PÅ HVILKEN KANAL, slik
 * at kalleren faktisk kan varsle på den — ikke bare vite at «noe» ble fortrengt.
 */
export function fortrengtLinjer(h: Hoerbart, vakter: readonly Kanalvakt[]): string[] {
  const navnFor = new Map(vakter.map((k) => [k.kanalId, k.navn] as const));
  return h.fortrengt.map((f) => `${f.fra} på ${navnFor.get(f.kanalId) ?? f.kanalId} — FORTRENGT, hørtes ikke.`);
}

export class KanaloversiktPanel {
  readonly el: HTMLElement;
  private sokEl!: HTMLInputElement;
  private statusEl!: HTMLElement;
  private vaktbetegnelseEl!: HTMLElement;
  private aktivEl!: HTMLElement;
  private aktivKildeEl!: HTMLElement;
  private fortrengtEl!: HTMLElement;
  private sendeEl!: HTMLElement;
  private allCallEl!: HTMLElement;
  private listeEl!: HTMLElement;
  private data: KanaloversiktData | null = null;
  private sok = '';
  // ⚠ «STILLE» ER STANDARDVERDIEN, IKKE ET FUNN. Uten `hentAktive` (se opts) VET vi
  // ikke om noe høres — men å vise noe annet enn stille uten data ville vært en
  // påstand vi ikke har grunnlag for (samme doktrine som «tom vaktliste» i lageret).
  private hoerbart: Hoerbart = { spiller: null, fortrengt: [], grunn: '' };
  private sendeStatus: { kan: boolean; grunn: string } = { kan: false, grunn: '' };
  private taleDegraderingEl!: HTMLElement;
  // ⚠ TILSTANDEN BÆRES MELLOM OMFRISKER — hysteresen i taleDegraderingsnivaa
  // krever forrige nivå for å telle oppgraderings-bekreftelser (se filhodet i
  // taledegradering.ts). Uten dette ville panelet «glemt» historikken ved hver
  // oppfrisk() og aldri fått hysterese-vernet mot flipp-flopp.
  private taleTilstand: TaleDegraderingsTilstand | undefined;
  private taleLinje = '';

  constructor(private opts: KanaloversiktOpts) {
    this.el = this.bygg();
  }

  toggle(): void {
    const aapen = this.el.classList.toggle('open');
    if (aapen) void this.oppfrisk();
  }

  async oppfrisk(): Promise<void> {
    try {
      this.data = await this.opts.hent();
    } catch {
      this.data = null;
    }
    // ⚠ AKTIVE SENDINGER HENTES SEPARAT OG FEILER STILLE TIL «TOM». `hentAktive` er
    // valgfri (se opts-dokumentasjonen — ingen ekte kilde i COP før PTT-05), og en
    // feilende hentAktive skal ikke velte hele panelet, bare gi «stille» for denne
    // omfrisken — nøyaktig som en radio uten antenne ikke viser en falsk sending.
    let aktive: AktivSending[] = [];
    if (this.opts.hentAktive) {
      try {
        aktive = await this.opts.hentAktive();
      } catch { aktive = []; }
    }
    if (this.data) {
      this.hoerbart = hvaHoresNaa(this.data.vakter, aktive);
      this.sendeStatus = kanSende(this.data.vakter, aktive, this.opts.meg ?? '');
    } else {
      this.hoerbart = { spiller: null, fortrengt: [], grunn: '' };
      this.sendeStatus = { kan: false, grunn: '' };
    }

    // ── TALE-DEGRADERINGSNIVÅ (PTT-13) — HENTES SEPARAT, FEILER STILLE ──────
    // Samme mønster som `hentAktive` over: valgfri kilde, en feilende/manglende
    // måling beholder forrige kjente linje uendret — IKKE et gjettet nivå, og
    // IKKE et falskt fall (en radio uten fersk måling viser sist kjente status,
    // den later ikke som den vet noe nytt, men lyver heller ikke om at den er død).
    if (this.opts.hentTaleLink) {
      let kvalitet: LinkQuality | null = null;
      try {
        kvalitet = await this.opts.hentTaleLink();
      } catch { kvalitet = null; }
      if (kvalitet) {
        const r = taleDegraderingsnivaa(kvalitet, this.taleTilstand);
        this.taleTilstand = r.tilstand;
        this.taleLinje = taleDegraderingsLinje(r.nivaa, kvalitet);
      }
    } else {
      this.taleTilstand = undefined;
      this.taleLinje = '';
    }
    this.tegn();
  }

  private bygg(): HTMLElement {
    const p = document.createElement('aside');
    p.className = 'kanal-panel';

    const head = document.createElement('div');
    head.className = 'kanal-head';
    const t = document.createElement('span');
    t.className = 'section-label';
    t.textContent = 'Kanaler (PTT)';
    const x = document.createElement('button');
    x.type = 'button';
    x.className = 'kanal-x';
    x.textContent = '×';
    x.onclick = () => this.el.classList.remove('open');
    head.append(t, x);
    p.appendChild(head);

    this.statusEl = document.createElement('div');
    this.statusEl.className = 'kanal-status';
    this.statusEl.setAttribute('role', 'status');
    p.appendChild(this.statusEl);

    // ── DUALWATCH/TRIWATCH + «HVA HØRES NÅ» (PTT-14) ────────────────────────
    // ⚠ TX/RX-TALLET OG PRESEDENSEN STÅR HER, SAMLET — operatøren skal se HVOR
    // mange nett hun vokter og HVORFOR den som snakker akkurat nå får ordet, i
    // samme blikk som resten av vaktlista, ikke i et eget vindu hun må huske å åpne.
    this.vaktbetegnelseEl = document.createElement('div');
    this.vaktbetegnelseEl.className = 'kanal-vaktbetegnelse';
    p.appendChild(this.vaktbetegnelseEl);

    // ⚠ TALE-DEGRADERINGSNIVÅ (PTT-13) — SKJULT SÅ LENGE VI IKKE HAR EN
    // MÅLING. Ingen falsk «full kvalitet»-standardtekst; se opts.hentTaleLink.
    this.taleDegraderingEl = document.createElement('div');
    this.taleDegraderingEl.className = 'kanal-tale-degradering';
    this.taleDegraderingEl.hidden = true;
    p.appendChild(this.taleDegraderingEl);

    this.aktivEl = document.createElement('div');
    this.aktivEl.className = 'kanal-aktiv';
    this.aktivEl.setAttribute('role', 'status');
    p.appendChild(this.aktivEl);

    // ⚠ EN SYNLIG, ÆRLIG NOTIS NÅR DET IKKE FINNES EN SANNTIDS-KILDE (se opts).
    // Skjules automatisk den dagen `hentAktive` er koblet — ikke fjern manuelt.
    this.aktivKildeEl = document.createElement('div');
    this.aktivKildeEl.className = 'kanal-aktiv-kilde';
    this.aktivKildeEl.hidden = true;
    p.appendChild(this.aktivKildeEl);

    // ⚠ FORTRENGT-BANNERET — `role="alert"`, IKKE `role="status"`. En fortrengt
    // sending er tapt trafikk (filhodet i pttvakt.ts), og skal RIVE i operatøren
    // på samme måte som en radio-kollisjon gjør — en stille statusoppdatering ville
    // vært nøyaktig den digitale stillheten som er problemet.
    this.fortrengtEl = document.createElement('div');
    this.fortrengtEl.className = 'kanal-fortrengt';
    this.fortrengtEl.setAttribute('role', 'alert');
    this.fortrengtEl.hidden = true;
    p.appendChild(this.fortrengtEl);

    this.sendeEl = document.createElement('div');
    this.sendeEl.className = 'kanal-sende';
    p.appendChild(this.sendeEl);

    this.allCallEl = document.createElement('div');
    this.allCallEl.className = 'kanal-allcall-info';
    this.allCallEl.hidden = true;
    p.appendChild(this.allCallEl);

    this.sokEl = document.createElement('input');
    this.sokEl.type = 'search';
    this.sokEl.className = 'kanal-sok';
    this.sokEl.placeholder = 'Søk i kanaler …';
    this.sokEl.oninput = () => { this.sok = this.sokEl.value; this.tegn(); };
    p.appendChild(this.sokEl);

    this.listeEl = document.createElement('div');
    this.listeEl.className = 'kanal-liste';
    p.appendChild(this.listeEl);

    // ── Egen gruppe ──────────────────────────────────────────────────────
    // Eier: «at man kan opprette egne grupper». Minimalt: id + navn, lagt til i
    // egen vaktliste. ⚠ Å opprette en gruppe er IKKE det samme som å opprette en
    // Matrix-kanal — dette legger til en kanal i MIN vaktliste. Det er en ærlig
    // delmengde, og teksten sier det.
    const ny = document.createElement('div');
    ny.className = 'kanal-ny';
    const idInn = document.createElement('input');
    idInn.className = 'kanal-inn';
    idInn.placeholder = 'kanal-id';
    const navnInn = document.createElement('input');
    navnInn.className = 'kanal-inn';
    navnInn.placeholder = 'Visningsnavn';
    const leggKnapp = document.createElement('button');
    leggKnapp.type = 'button';
    leggKnapp.className = 'kanal-btn';
    leggKnapp.textContent = '+ Legg til';
    leggKnapp.onclick = () => {
      const id = idInn.value.trim();
      if (!id) { this.opts.varsle('Kanal-id kreves.'); return; }
      void this.utfor(() => this.opts.leggTil(id, navnInn.value.trim() || id));
      idInn.value = ''; navnInn.value = '';
    };
    ny.append(idInn, navnInn, leggKnapp);
    p.appendChild(ny);

    if (this.opts.allCall) {
      const ac = document.createElement('div');
      ac.className = 'kanal-allcall';
      const merke = document.createElement('span');
      merke.className = 'kanal-allcall-merke';
      merke.textContent = 'ALL CALL';
      const grunnInn = document.createElement('input');
      grunnInn.className = 'kanal-inn';
      grunnInn.placeholder = 'Grunn (vises for alle)';
      const txBoks = document.createElement('label');
      txBoks.className = 'kanal-tx-boks';
      const txHake = document.createElement('input');
      txHake.type = 'checkbox';
      txBoks.append(txHake, document.createTextNode(' også senderett'));
      const knapp = document.createElement('button');
      knapp.type = 'button';
      knapp.className = 'kanal-btn fare';
      knapp.textContent = 'Sett alle på valgt kanal';
      // ⚠ TITTELEN SIER HVA ORDREN KAN OG IKKE KAN. En kommandofunksjon som ikke
      // forklarer sin egen grense inviterer til å bli trodd om mer enn den gjør.
      knapp.title = 'Tvinger alle i distriktet til å LYTTE på kanalen. '
        + 'Kan aldri åpne mikrofonen deres, og kan ikke fjerne nødkanalen.';
      knapp.onclick = () => {
        const valgt = this.listeEl.querySelector<HTMLElement>('.kanal-rad.valgt');
        const id = valgt?.dataset.kanalId ?? '';
        const navn = valgt?.dataset.navn ?? id;
        if (!id) { this.opts.varsle('Velg en kanal i lista først.'); return; }
        void this.utfor(() => this.opts.allCall!(id, navn, grunnInn.value.trim(), txHake.checked));
        grunnInn.value = ''; txHake.checked = false;
      };
      ac.append(merke, grunnInn, txBoks, knapp);
      p.appendChild(ac);
    }
    return p;
  }

  private async utfor(f: () => Promise<string | null>): Promise<void> {
    try {
      const melding = await f();
      if (melding) this.opts.varsle(melding);
    } catch (e) {
      this.opts.varsle(e instanceof Error ? e.message : String(e));
    }
    await this.oppfrisk();
  }

  private tegn(): void {
    const d = this.data;
    if (!d) {
      // ⚠ EN HENTEFEIL ER IKKE «INGEN KANALER». Sto det tomt, ville operatøren tenkt
      // at hun ikke er med i noe — mens sannheten er at vi ikke vet.
      this.statusEl.textContent = '⚠ Kunne ikke lese vaktlista fra hubben — dette er IKKE det samme som at du står uten kanaler.';
      this.statusEl.classList.add('advarsel');
      this.vaktbetegnelseEl.textContent = '';
      this.taleDegraderingEl.hidden = true;
      this.aktivEl.textContent = '';
      this.aktivEl.className = 'kanal-aktiv';
      this.aktivKildeEl.hidden = true;
      this.fortrengtEl.hidden = true;
      this.sendeEl.textContent = '';
      this.listeEl.replaceChildren();
      return;
    }
    const st = kanalStatus(d);
    this.statusEl.textContent = d.advarsel ? `${st.tekst} · ${d.advarsel}` : st.tekst;
    this.statusEl.classList.toggle('advarsel', st.advarsel || !!d.advarsel);

    // ── DUALWATCH/TRIWATCH-BETEGNELSE ───────────────────────────────────────
    const dw = dualwatchOppsummering(d.vakter, d.maksRx);
    this.vaktbetegnelseEl.textContent = `${dw.betegnelse} · ${dw.antall}/${dw.maksRx} kanaler`;

    // ── TALE-DEGRADERINGSNIVÅ (PTT-13) — bare vist når vi HAR en måling ──────
    this.taleDegraderingEl.hidden = !this.taleLinje;
    this.taleDegraderingEl.textContent = this.taleLinje;

    // ── HVA HØRES NÅ, OG HVORFOR (presedens) ────────────────────────────────
    const merke = presedensMerke(this.hoerbart, d.vakter);
    this.aktivEl.className = `kanal-aktiv presedens-${merke}`;
    this.aktivEl.textContent = hoerbartLinje(this.hoerbart);

    // ⚠ ÆRLIG NOTIS NÅR DET IKKE FINNES EN SANNTIDS-KILDE — se opts.hentAktive.
    // «Stille» over er da IKKE et funn, det er det vi VET (ingenting), og notisen
    // sier det rett ut i stedet for å la operatøren tro sambandet er tomt.
    this.aktivKildeEl.hidden = !!this.opts.hentAktive;
    if (!this.opts.hentAktive) {
      this.aktivKildeEl.textContent = 'Ingen sanntids-talekanal koblet i COP ennå (venter på PTT-05/LiveKit) — '
        + 'vises som stille til den finnes, ikke fordi vi vet at det ER stille.';
    }

    // ── FORTRENGT — TAPT TRAFIKK, VARSLES EKSPLISITT ────────────────────────
    const fortrengt = fortrengtLinjer(this.hoerbart, d.vakter);
    this.fortrengtEl.hidden = fortrengt.length === 0;
    this.fortrengtEl.replaceChildren();
    if (fortrengt.length) {
      const tittel = document.createElement('div');
      tittel.className = 'kanal-fortrengt-tittel';
      tittel.textContent = `⚠ FORTRENGT (${fortrengt.length}) — hørtes ikke:`;
      this.fortrengtEl.appendChild(tittel);
      for (const linje of fortrengt) {
        const rad = document.createElement('div');
        rad.className = 'kanal-fortrengt-rad';
        rad.textContent = linje;
        this.fortrengtEl.appendChild(rad);
      }
    }

    // ── KAN JEG SENDE NÅ (halv dupleks) ─────────────────────────────────────
    this.sendeEl.textContent = this.sendeStatus.grunn;
    this.sendeEl.classList.toggle('advarsel', !this.sendeStatus.kan);

    const ac = allCallTekst(d.sisteAllCall, Date.now());
    this.allCallEl.hidden = !ac;
    this.allCallEl.textContent = ac;

    this.listeEl.replaceChildren();
    const vis = sorterForVisning(filtrerKanaler(d.vakter, this.sok));
    if (!vis.length) {
      const tom = document.createElement('div');
      tom.className = 'kanal-tom';
      // ⚠ TO ULIKE TOMTILSTANDER. «Ingen kanaler» og «søket ga ingen treff» er
      // forskjellige situasjoner med ulike neste steg.
      tom.textContent = d.vakter.length
        ? `Ingen treff på «${this.sok}» — ${d.vakter.length} kanaler er skjult av søket.`
        : 'Du er ikke med i noen kanal ennå. Legg til én under.';
      this.listeEl.appendChild(tom);
      return;
    }
    for (const k of vis) this.listeEl.appendChild(this.rad(k, d.vakter));
  }

  private rad(k: Kanalvakt, alle: readonly Kanalvakt[]): HTMLElement {
    const rad = document.createElement('div');
    rad.className = 'kanal-rad';
    rad.dataset.kanalId = k.kanalId;
    rad.dataset.navn = k.navn;
    if (k.rolle === 'tx') rad.classList.add('tx');
    if (k.nod) rad.classList.add('nod');
    if (k.paatvungetAv) rad.classList.add('paalagt');
    rad.onclick = () => {
      for (const r of this.listeEl.querySelectorAll('.kanal-rad')) r.classList.remove('valgt');
      rad.classList.add('valgt');
    };

    const merke = document.createElement('span');
    merke.className = 'kanal-merke';
    merke.textContent = kanalMerke(k);
    const navn = document.createElement('span');
    navn.className = 'kanal-navn';
    navn.textContent = k.navn;
    rad.append(merke, navn);

    if (k.paatvungetAv) {
      const av = document.createElement('span');
      av.className = 'kanal-paalagt-av';
      av.textContent = `pålagt av ${k.paatvungetAv}`;
      rad.appendChild(av);
    }

    const handlinger = document.createElement('span');
    handlinger.className = 'kanal-handlinger';
    if (k.rolle !== 'tx') {
      const tx = document.createElement('button');
      tx.type = 'button';
      tx.className = 'kanal-btn liten';
      tx.textContent = 'Send her';
      tx.title = 'Flytt senderetten hit. Bare én kanal kan ha den.';
      tx.onclick = (e) => { e.stopPropagation(); void this.utfor(() => this.opts.settTx(k.kanalId)); };
      handlinger.appendChild(tx);
    }
    const kf = kanFjerne(k, alle);
    const fjern = document.createElement('button');
    fjern.type = 'button';
    fjern.className = 'kanal-btn liten';
    fjern.textContent = '×';
    // ⚠ DEAKTIVERT MED EN FORKLARING framfor en knapp som feiler ved klikk.
    fjern.disabled = !kf.kan;
    fjern.title = kf.kan ? `Fjern ${k.navn}` : kf.grunn;
    fjern.onclick = (e) => { e.stopPropagation(); void this.utfor(() => this.opts.fjern(k.kanalId)); };
    handlinger.appendChild(fjern);
    rad.appendChild(handlinger);
    return rad;
  }
}
